import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const dashPath = !user?.role ? "/choose-role" : user.role === "candidate" ? "/candidate" : user.role === "admin" ? "/admin" : "/company";

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <img src="/assets/logo.png" alt="Global Line Services" className="h-9 w-auto" />
        </Link>

        <nav className="hidden items-center gap-6 md:flex">
          <Link to="/jobs" data-testid="nav-jobs-link" className="text-sm font-semibold text-slate-700 transition-colors hover:text-sky-600">
            {t("nav.jobs")}
          </Link>
          {user && (
            <Link to={dashPath} data-testid="nav-dashboard-link" className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 transition-colors hover:text-sky-600">
              <LayoutDashboard className="h-4 w-4" strokeWidth={2} /> {t("nav.dashboard")}
            </Link>
          )}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher id="language-switcher-desktop" />
          {user ? (
            <button
              onClick={handleLogout}
              data-testid="nav-logout-button"
              className="flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-4 py-1.5 text-sm font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,1)]"
            >
              <LogOut className="h-4 w-4" strokeWidth={2} /> {t("nav.logout")}
            </button>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-button"
                className="rounded-full border-2 border-slate-900 bg-white px-4 py-1.5 text-sm font-semibold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,1)]"
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-button"
                className="rounded-full border-2 border-slate-900 bg-sky-400 px-4 py-1.5 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[3px_3px_0px_0px_rgba(15,23,42,1)]"
              >
                {t("nav.register")}
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher id="language-switcher-mobile" />
          <button data-testid="nav-mobile-menu-button" onClick={() => setOpen(!open)} className="rounded-md border-2 border-slate-900 p-1.5">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-3">
            <Link to="/jobs" onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-700">{t("nav.jobs")}</Link>
            {user && <Link to={dashPath} onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-700">{t("nav.dashboard")}</Link>}
            {user ? (
              <button onClick={handleLogout} className="text-left text-sm font-semibold text-red-600">{t("nav.logout")}</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-700">{t("nav.login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm font-bold text-sky-600">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
